import { useEffect, useState } from "react";
import { Plus, Trash2, Check, Upload, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const EMPTY = { name: "", text: "", rating: 5, photo: "", approved: true };

export default function TestimonialsAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Image under 2MB");
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, photo: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/testimonials", { ...form, rating: Number(form.rating) });
      toast.success("Review added");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch { toast.error("Failed"); }
  };

  const toggleApprove = async (t) => {
    await api.put(`/admin/testimonials/${t.id}`, { approved: !t.approved });
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete review?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    load();
  };

  return (
    <div data-testid="admin-testimonials">
      <div className="flex justify-between items-center mb-8">
        <div><h1 className="font-serif text-3xl text-espresso">Reviews</h1><p className="text-bark text-sm">{items.length} testimonials</p></div>
        <Button onClick={() => setOpen(true)} data-testid="add-testimonial-button" className="bg-terracotta text-bone hover:bg-espresso"><Plus className="w-4 h-4 mr-1" /> Add review</Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {items.map((t) => (
          <div key={t.id} className="border border-sandline rounded-xl p-5" data-testid={`testimonial-row-${t.id}`}>
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center gap-2">
                {t.photo ? <img src={t.photo} alt="" className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-terracotta/15 text-terracotta flex items-center justify-center text-sm">{t.name?.[0]}</div>}
                <span className="text-espresso font-medium">{t.name}</span>
              </div>
              <span className="text-terracotta text-sm">{"★".repeat(t.rating)}</span>
            </div>
            <p className="text-bark text-sm mb-3">“{t.text}”</p>
            <div className="flex items-center gap-2">
              <Button size="sm" variant={t.approved ? "default" : "outline"} onClick={() => toggleApprove(t)} data-testid={`approve-testimonial-${t.id}`} className={t.approved ? "bg-moss text-bone h-8" : "h-8 border-sandline"}>
                <Check className="w-3.5 h-3.5 mr-1" /> {t.approved ? "Approved" : "Approve"}
              </Button>
              <button onClick={() => del(t.id)} data-testid={`delete-testimonial-${t.id}`} className="p-1.5 text-bark hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="p-5 text-bark text-sm">No reviews yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-bone w-[calc(100%-1rem)] max-h-[92vh] overflow-y-auto p-5 md:p-6" data-testid="testimonial-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-espresso">Add review</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div><label className="text-sm text-bark mb-1 block">Customer name</label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="testimonial-name-input" className="border-sandline" /></div>
            <div><label className="text-sm text-bark mb-1 block">Review</label><Textarea required value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="testimonial-text-input" className="border-sandline" rows={3} /></div>
            <div><label className="text-sm text-bark mb-1 block">Rating (1-5)</label><Input type="number" min="1" max="5" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} className="border-sandline w-24" /></div>
            <div>
              <label className="text-sm text-bark mb-1 block">Photo (optional)</label>
              <div className="flex items-center gap-3">
                {form.photo && <div className="relative w-14 h-14"><img src={form.photo} alt="" className="w-full h-full object-cover rounded-full" /><button type="button" onClick={() => setForm({ ...form, photo: "" })} className="absolute -top-1 -right-1 bg-espresso text-bone rounded-full p-0.5"><X className="w-3 h-3" /></button></div>}
                <label className="w-14 h-14 border border-dashed border-sandline rounded-full flex items-center justify-center cursor-pointer hover:border-terracotta"><Upload className="w-4 h-4 text-bark" /><input type="file" accept="image/*" className="hidden" onChange={handleFile} /></label>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-espresso"><Switch checked={form.approved} onCheckedChange={(v) => setForm({ ...form, approved: v })} /> Show on site</label>
            <DialogFooter><Button type="submit" data-testid="save-testimonial-button" className="bg-terracotta text-bone hover:bg-espresso">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
